import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import Trans from '@/i18n/translation'

// Configuración de regiones disponibles
export const REGIONS = {
  ES: {
    id: 'es',
    name: 'España',
    flag: '🇪🇸',
    currency: '€',
    defaultLanguage: 'es',
    cities: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Bilbao', 'Málaga'],
    mainCity: 'Madrid',
    timezone: 'Europe/Madrid',
    phone: '+34'
  },
  US: {
    id: 'us',
    name: 'United States',
    shortName: 'New York',
    flag: '🇺🇸',
    currency: '$',
    defaultLanguage: 'en',
    cities: ['New York'],
    mainCity: 'New York',
    timezone: 'America/New_York',
    phone: '+1'
  },
  UK: {
    id: 'uk',
    name: 'United Kingdom',
    shortName: 'London',
    flag: '🇬🇧',
    currency: '£',
    defaultLanguage: 'en',
    cities: ['London'],
    mainCity: 'London',
    timezone: 'Europe/London',
    phone: '+44'
  }
}

// Estado global de la región (singleton pattern)
const currentRegion = ref(null)
const isRegionInitialized = ref(false)

// Detecta región desde el idioma del navegador (fallback sin red)
// Lógica mejorada: mira TODOS los idiomas del navegador (navigator.languages),
// no solo el primero, para distinguir en-US de en-GB con mayor fiabilidad
function detectRegionFromBrowser(path = '') {
  const langs = (navigator.languages?.length ? navigator.languages : [navigator.language])
    .map(l => l.toLowerCase())
  const primary = langs[0]
  const isFindYourColivingHub = path.startsWith('/findyourcoliving')

  // Español → siempre España
  if (primary.startsWith('es')) return REGIONS.ES

  // Inglés con country code explícito — comprobamos todos los idiomas de la lista
  // para capturar casos como ['en', 'en-US'] o ['en-US', 'en']
  const hasEnUS = langs.some(l => l === 'en-us' || l.startsWith('en-us'))
  const hasEnGB = langs.some(l => l === 'en-gb' || l.startsWith('en-gb'))

  if (hasEnUS && !hasEnGB) return REGIONS.US
  if (hasEnGB && !hasEnUS) return REGIONS.UK

  // Ambos presentes: el primero en la lista gana
  if (hasEnUS && hasEnGB) {
    return langs.findIndex(l => l.startsWith('en-us')) < langs.findIndex(l => l.startsWith('en-gb'))
      ? REGIONS.US
      : REGIONS.UK
  }

  // Inglés genérico sin country code (ej. solo 'en'):
  // - Hub internacional → US (NYC) hasta que IP refine US vs UK
  // - Resto del sitio → España (mercado principal)
  if (primary.startsWith('en')) {
    return isFindYourColivingHub ? REGIONS.US : REGIONS.ES
  }

  return REGIONS.ES
}

// Detecta región real por IP usando el endpoint /api/geo del backend.
// Firebase CDN inyecta x-appengine-country en cada request, el backend lo devuelve
// sin llamar a ninguna API externa.
async function detectRegionFromIP() {
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 1500)
    const res = await fetch('/api/geo', { signal: controller.signal, cache: 'no-store' })
    clearTimeout(timeout)
    if (!res.ok) return null
    const { region } = await res.json()
    return region ? REGIONS[region.toUpperCase()] ?? null : null
  } catch {
    return null
  }
}

export function useRegion() {
  const router = useRouter()

  // Ciudades ES principales (para detectar /coliving/madrid, etc.)
  const ES_CITY_SLUGS = new Set([
    'madrid', 'barcelona', 'valencia', 'sevilla', 'bilbao', 'malaga', 'málaga',
    'salamanca', 'alicante', 'granada', 'zaragoza', 'las-palmas', 'tenerife', 'pamplona',
  ])

  // Deduce la región a partir de la URL actual:
  // /encuentratucoliving → es, /findyourcoliving → en, /coliving/:ciudadES → es
  function detectRegionFromRoute() {
    const path = window.location.pathname
    if (path.startsWith('/encuentratucoliving')) return REGIONS.ES
    if (path.startsWith('/findyourcoliving')) return null // dejar que IP/browser decida US vs UK

    // /coliving/:slug — landing Google Ads y escaparates
    const colivingMatch = path.match(/^\/coliving\/([^/]+)/)
    if (colivingMatch && ES_CITY_SLUGS.has(colivingMatch[1].toLowerCase())) {
      return REGIONS.ES
    }

    // /residencias (PBSA), legacy /residencias-estudiantes → España
    if (path.startsWith('/residencias')) return REGIONS.ES

    return null
  }

  // Inicializar región:
  // - Si el usuario eligió región MANUALMENTE → respetar siempre (no re-detectar)
  // - Si la región fue AUTO-DETECTADA → re-detectar cada vez por si cambió el idioma
  // - Primera visita → ruta > IP geolocation > browser language
  const initializeRegion = async () => {
    if (isRegionInitialized.value) {
      return
    }

    const savedRegion = localStorage.getItem('user-region')
    const savedSource = localStorage.getItem('user-region-source') // 'manual' | 'auto' | null

    // 1. Usuario recurrente que eligió región MANUALMENTE → respetarla
    if (savedRegion && savedSource === 'manual' && REGIONS[savedRegion.toUpperCase()]) {
      currentRegion.value = REGIONS[savedRegion.toUpperCase()]
      isRegionInitialized.value = true
      return
    }

    // 2. La URL es la señal más fuerte: /encuentratucoliving = España siempre
    const path = window.location.pathname
    const routeRegion = detectRegionFromRoute()

    // 3. Región provisional por idioma del navegador (instantáneo, sin red)
    const browserRegion = detectRegionFromBrowser(path)
    currentRegion.value = routeRegion || browserRegion
    isRegionInitialized.value = true

    // 4. Refinar con geolocalización real por IP (asíncrono, más fiable)
    const ipRegion = await detectRegionFromIP()

    // Prioridad final: ruta > IP > browser. Si la ruta ya fijó ES, no sobrescribir.
    const finalRegion = routeRegion || ipRegion || browserRegion
    if (finalRegion.id !== currentRegion.value?.id) {
      currentRegion.value = finalRegion
    }

    // Persistir como 'auto' (puede ser sobreescrito en futuras visitas si cambia idioma/IP)
    localStorage.setItem('user-region', currentRegion.value.id)
    localStorage.setItem('user-region-source', 'auto')
  }

  // Cambiar región manualmente (llamado desde RegionSelector, navbar mobile, etc.)
  // Marca la elección como 'manual' para que no sea sobreescrita por auto-detección.
  // Cambia también el idioma de la app según el defaultLanguage de la región.
  const setRegion = (regionId) => {
    const region = REGIONS[regionId.toUpperCase()]
    if (!region) {
      console.warn(`Región ${regionId} no encontrada`)
      return
    }

    console.log('🌍 Cambiando región a:', region.name)
    currentRegion.value = region
    localStorage.setItem('user-region', region.id)
    localStorage.setItem('user-region-source', 'manual') // ← respetado siempre

    // Cambiar idioma automáticamente según la región (es → español, us/uk → inglés)
    const targetLocale = region.defaultLanguage // 'es' | 'en'
    if (Trans.currentLocale !== targetLocale) {
      Trans.switchLanguage(targetLocale)
    }

    // Emitir evento personalizado para que otros componentes reaccionen
    window.dispatchEvent(new CustomEvent('region-changed', { 
      detail: { region: region.id, locale: targetLocale } 
    }))
    
    console.log('✅ Región actualizada:', region.name, '| Idioma:', targetLocale)
  }

  // Computed properties útiles
  const regionId = computed(() => currentRegion.value?.id || 'es')
  const regionName = computed(() => currentRegion.value?.name || 'España')
  const regionShortName = computed(() => currentRegion.value?.shortName || currentRegion.value?.name || 'España')
  const regionFlag = computed(() => currentRegion.value?.flag || '🇪🇸')
  const regionCurrency = computed(() => currentRegion.value?.currency || '€')
  const regionCities = computed(() => currentRegion.value?.cities || [])
  const regionMainCity = computed(() => currentRegion.value?.mainCity || 'Madrid')
  const regionDefaultLanguage = computed(() => currentRegion.value?.defaultLanguage || 'es')

  // Formatear precio según región
  const formatPrice = (amount) => {
    if (!currentRegion.value) return `${amount}€`

    const currency = currentRegion.value.currency
    const currencyFirst = currency === '$' // Dólar va antes del número

    return currencyFirst 
      ? `${currency}${amount.toLocaleString()}` 
      : `${amount.toLocaleString()}${currency}`
  }

  // Obtener colivings filtrados por región (para uso futuro)
  const getRegionFilterParams = () => {
    if (!currentRegion.value) return {}

    return {
      region: currentRegion.value.id,
      cities: currentRegion.value.cities
    }
  }

  // Observar cambios de región
  watch(currentRegion, (newRegion) => {
    if (newRegion) {
      // Analytics: trackear cambio de región
      if (window.gtag) {
        window.gtag('event', 'region_change', {
          region: newRegion.id,
          region_name: newRegion.name
        })
      }
    }
  })

  return {
    // Estado
    currentRegion: computed(() => currentRegion.value),
    regionId,
    regionName,
    regionShortName,
    regionFlag,
    regionCurrency,
    regionCities,
    regionMainCity,
    regionDefaultLanguage,
    isRegionInitialized,

    // Métodos
    initializeRegion,
    setRegion,
    formatPrice,
    getRegionFilterParams,

    // Constantes
    REGIONS
  }
}
